import { DataGrid } from '@mui/x-data-grid';
import ColoredAvatar from 'core/components/ColoredAvatar';
import { useAsync } from 'hooks/useAsync';
import CardedPage from 'layout/pages/CardedPage';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as clientService from 'services/clientService';
import ClientCell from './ClientCell';
import ClientsPageHeader from './ClientsPageHeader';
import type { GridColDef, GridEventListener } from '@mui/x-data-grid';
import type { Client } from 'models/client/client';

export default function ClientsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = useState({
    page: +(searchParams.get('pageIndex') ?? 0),
    pageSize: +(searchParams.get('pageSize') ?? 25),
  });
  const [searchText, setSearchText] = useState(searchParams.get('searchText') ?? '');
  const navigate = useNavigate();

  const queryOptions = useMemo(
    () => ({
      pageIndex: paginationModel.page,
      pageSize: paginationModel.pageSize,
      filterText: searchText,
    }),
    [paginationModel, searchText]
  );

  const { loading, result } = useAsync(clientService.getClientsAsync, [queryOptions]);
  const [rowCountState, setRowCountState] = useState(result?.totalCount ?? 0);

  useEffect(() => {
    setRowCountState((prevRowCountState: number) =>
      !isNil(result?.totalCount) ? result?.totalCount ?? 0 : prevRowCountState
    );
  }, [result?.totalCount, setRowCountState]);

  useEffect(() => {
    setSearchParams({
      pageIndex: String(queryOptions.pageIndex),
      pageSize: String(queryOptions.pageSize),
      searchText: queryOptions.filterText,
    });
  }, [setSearchParams, queryOptions]);

  const columns: Array<GridColDef<Client>> = [
    {
      field: 'avatar',
      width: 60,
      display: 'flex',
      renderCell: (params) => (
        <ColoredAvatar alt={params.row.surname}>{params.row.surname[0]}</ColoredAvatar>
      ),
    },
    {
      field: 'name',
      flex: 1,
      display: 'flex',
      renderCell: (params) => (
        <div>
          <ClientCell data={params.row} />
        </div>
      ),
    },
  ];

  const rowClickHandle: GridEventListener<'rowClick'> = (params) => {
    navigate(String(params.row.id));
  };

  return (
    <CardedPage
      title='Клиенты'
      disablePadding
      header={
        <ClientsPageHeader onSearchChanged={setSearchText} searchTextInit={searchText} />
      }>
      <DataGrid
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            border: 'none',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        rows={result?.items ?? []}
        rowCount={rowCountState}
        loading={loading}
        disableColumnMenu={true}
        rowHeight={70}
        autoHeight
        hideFooterSelectedRowCount
        onRowClick={rowClickHandle}
        columnHeaderHeight={0}
        paginationModel={paginationModel}
        paginationMode='server'
        onPaginationModelChange={setPaginationModel}
        columns={columns}
      />
    </CardedPage>
  );
}
